
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TableroGeneral from '../components/reports/TableroGeneral';
import VentasSection from '../components/reports/VentasSection';
import CajaSection from '../components/reports/CajaSection';
import PersonalSection from '../components/reports/PersonalSection';
import ClientesSection from '../components/reports/ClientesSection';
import VehiculosSection from '../components/reports/VehiculosSection';
import TurnosSection from '../components/reports/TurnosSection';
import ItemsSection from '../components/reports/ItemsSection';
import MargenSection from '../components/reports/MargenSection';
import DiagnosticosSection from '../components/reports/DiagnosticosSection';
import ComprasSection from '../components/reports/ComprasSection';
import GastosSection from '../components/reports/GastosSection';
import InsumosSection from '../components/reports/InsumosSection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { Tip } from '../components/ui/tip';
import { cn } from '../lib/utils';
import { openPdf } from '../utils/pdfHelpers';
import { getAppSocket } from '../lib/wesSocket';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../components/ui/chart';

type KpiValue = number | { value: number; deltaPct?: number | null; previous?: number } | null | undefined;

type EfficiencySupply = {
  supplyId: string; supplyName: string; supplyUnit: string; costPrice: number;
  totalConsumed: number; totalServices: number; yield: number; costPerService: number;
  linkedServices: { serviceName: string; count: number }[];
};
type EfficiencyData = {
  supplies: EfficiencySupply[];
  summary: { totalServices: number; totalConsumed: number; avgYield: number; avgCostPerService: number };
  monthlyTrend: { month: string; services: number; consumed: number; yield: number }[];
  operatorBreakdown: { operatorId: string; operatorName: string; totalServices: number; percentage: number }[];
} | null;

type ReportsOverview = any;

const COLORS = ['#2563eb', '#16a34a', '#f97316', '#ef4444', '#a855f7', '#0ea5e9', '#e11d48', '#22c55e'];

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type RangeTypeValue = 'week' | 'month' | 'lastmonth' | 'quarter' | 'year' | 'lastyear' | 'custom';

const PERIOD_PRESETS: Array<[RangeTypeValue, string]> = [
  ['week', 'Semana actual'],
  ['month', 'Mes actual'],
  ['lastmonth', 'Mes pasado'],
  ['quarter', 'Trimestre actual'],
  ['year', 'Año actual'],
  ['lastyear', 'Año pasado']
];

// Selector de período unificado: un solo botón con presets + calendario de rango
const PeriodPicker = ({
  rangeType,
  startDate,
  endDate,
  onPreset,
  onRange
}: {
  rangeType: RangeTypeValue;
  startDate: string;
  endDate: string;
  onPreset: (type: RangeTypeValue) => void;
  onRange: (start: string, end: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  // Rango de dos clicks manejado a mano: primer click marca el "desde",
  // segundo click cierra el rango (la libreria resetea si ya hay rango completo)
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);

  const fmt = (s: string) =>
    s ? new Date(s + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
  // Si el rango personalizado es un año calendario completo, mostrarlo como "Año YYYY"
  const fullYearMatch = /^(\d{4})-01-01$/.exec(startDate || '');
  const isFullYear = !!fullYearMatch && endDate === `${fullYearMatch[1]}-12-31`;
  const label = rangeType === 'custom'
    ? (isFullYear ? `Año ${fullYearMatch![1]}` : `${fmt(startDate)} – ${fmt(endDate)}`)
    : (PERIOD_PRESETS.find(([v]) => v === rangeType)?.[1] || 'Período');

  // Años con datos (la planilla histórica arranca en 2022)
  const YEARS: number[] = [];
  for (let y = new Date().getFullYear(); y >= 2022; y--) YEARS.push(y);

  const selected = draftFrom
    ? { from: draftFrom, to: undefined }
    : {
        from: startDate ? new Date(startDate + 'T12:00:00') : undefined,
        to: endDate ? new Date(endDate + 'T12:00:00') : undefined
      };

  const handleDayClick = (day: Date) => {
    if (!draftFrom) {
      setDraftFrom(day);
      return;
    }
    const a = draftFrom <= day ? draftFrom : day;
    const b = draftFrom <= day ? day : draftFrom;
    onRange(isoLocal(a), isoLocal(b));
    setDraftFrom(null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDraftFrom(null); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="min-w-[220px] justify-start text-left font-normal">
          <CalendarIcon className="mr-2 h-4 w-4 opacity-60" />
          {label}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          <div className="flex flex-col gap-1 border-r p-2 min-w-[150px]">
            {PERIOD_PRESETS.map(([value, presetLabel]) => (
              <button
                key={value}
                type="button"
                onClick={() => { onPreset(value); setOpen(false); }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-left text-sm transition-colors',
                  rangeType === value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'
                )}
              >
                {presetLabel}
              </button>
            ))}
            <div className="mt-2 border-t pt-2 px-1">
              <Select
                value={isFullYear ? fullYearMatch![1] : ''}
                onValueChange={(y) => { onRange(`${y}-01-01`, `${y}-12-31`); setOpen(false); }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Año específico..." />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>Año {y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="mt-2 px-3 text-xs text-muted-foreground">
              o marcá un rango en el calendario
            </p>
          </div>
          <div>
            <Calendar
              mode="range"
              numberOfMonths={1}
              showOutsideDays={false}
              defaultMonth={selected.from ?? undefined}
              selected={selected as any}
              captionLayout="dropdown"
              fromYear={2020}
              toYear={new Date().getFullYear() + 1}
              onSelect={() => { /* la seleccion se maneja en onDayClick */ }}
              onDayClick={handleDayClick}
            />
            <p className="px-3 pb-2 text-xs text-muted-foreground">
              {draftFrom
                ? `Desde ${draftFrom.toLocaleDateString('es-AR')} — ahora tocá la fecha final`
                : 'Tocá la fecha de inicio del rango'}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(amount || 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(value || 0);

const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;

const getKpiValue = (kpi: KpiValue) => (typeof kpi === 'number' ? kpi : kpi?.value ?? 0);
const getKpiDelta = (kpi: KpiValue) => (typeof kpi === 'number' ? null : kpi?.deltaPct ?? null);

const KpiCard = ({
  label,
  value,
  deltaPct,
  format
}: {
  label: string;
  value: number;
  deltaPct?: number | null;
  format: (value: number) => string;
}) => {
  const deltaClass = deltaPct === null || deltaPct === undefined
    ? 'text-muted-foreground'
    : deltaPct >= 0
      ? 'text-emerald-600'
      : 'text-red-600';

  return (
    <div className="rounded-lg border p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold text-foreground">{format(value)}</div>
      {deltaPct !== null && deltaPct !== undefined && (
        <div className={`text-xs ${deltaClass}`}>
          {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}% vs anterior
        </div>
      )}
    </div>
  );
};

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border p-4 space-y-4">
    <div className="text-sm font-semibold text-foreground">{title}</div>
    {children}
  </div>
);

const EmptyState = ({ label }: { label: string }) => (
  <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground text-center">
    {label}
  </div>
);

const getDefaultRange = (type: 'week' | 'month' | 'quarter' | 'year') => {
  const now = new Date();
  let start: Date;
  let end: Date = new Date(now);

  if (type === 'week') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    start = new Date(now.getFullYear(), now.getMonth(), diff);
  } else if (type === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (type === 'quarter') {
    const quarterStart = Math.floor(now.getMonth() / 3) * 3;
    start = new Date(now.getFullYear(), quarterStart, 1);
  } else {
    start = new Date(now.getFullYear(), 0, 1);
  }

  return { start: isoLocal(start), end: isoLocal(end) };
};

const Reports: React.FC = () => {
  const initialRange = getDefaultRange('month');
  const [reportData, setReportData] = useState<ReportsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeType, setRangeType] = useState<RangeTypeValue>('month');
  const [activeTab, setActiveTab] = useState('resumen');
  const location = useLocation();

  // Permite llegar con la pestaña elegida desde otras pantallas (/reports?tab=ventas)
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t) setActiveTab(t);
  }, [location.search]);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [efficiencyData, setEfficiencyData] = useState<EfficiencyData>(null);

  /* ── Chart configs ── */
  const chartConfigValue: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigTotal: ChartConfig = { total: { label: "Total", color: "#16a34a" } };
  const chartConfigTotalCyan: ChartConfig = { total: { label: "Total", color: "#0ea5e9" } };
  const chartConfigTotalRed: ChartConfig = { total: { label: "Total", color: "#ef4444" } };
  const chartConfigTotalOrange: ChartConfig = { total: { label: "Total", color: "#f97316" } };
  const chartConfigTotalBlue: ChartConfig = { total: { label: "Total", color: "#2563eb" } };
  const chartConfigIncomeExpense: ChartConfig = {
    income: { label: "Ingresos", color: "#16a34a" },
    expense: { label: "Egresos", color: "#ef4444" }
  };
  const chartConfigPaymentMethods: ChartConfig = { total: { label: "Total", color: "#2563eb" } };
  const chartConfigBalance: ChartConfig = { value: { label: "Saldo", color: "#2563eb" } };
  const chartConfigNewVsReturning: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigActiveTrend: ChartConfig = { value: { label: "Clientes", color: "#16a34a" } };
  const chartConfigAppointments: ChartConfig = { value: { label: "Turnos", color: "#2563eb" } };
  const chartConfigAbsences: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigQuantity: ChartConfig = { quantity: { label: "Cantidad", color: "#2563eb" } };
  const chartConfigShare: ChartConfig = { quantity: { label: "Cantidad", color: "#2563eb" } };
  const chartConfigMargin: ChartConfig = { margin: { label: "Margen", color: "#16a34a" } };
  const chartConfigConsumption: ChartConfig = { quantity: { label: "Cantidad", color: "#2563eb" } };
  const chartConfigConsumptionTrend: ChartConfig = { value: { label: "Consumo", color: "#16a34a" } };
  const chartConfigEfficiency: ChartConfig = {
    services: { label: "Servicios", color: "#2563eb" },
    consumed: { label: "Consumido", color: "#f97316" },
    yield: { label: "Rendimiento", color: "#16a34a" }
  };
  const chartConfigRequests: ChartConfig = { requests: { label: "Pedidos", color: "#0ea5e9" } };
  const chartConfigRequestsTrend: ChartConfig = { value: { label: "Pedidos", color: "#2563eb" } };
  const chartConfigProviderPurchases: ChartConfig = { total: { label: "Total", color: "#2563eb" } };
  const chartConfigDependency: ChartConfig = { total: { label: "Total", color: "#2563eb" } };
  const chartConfigDebtTrend: ChartConfig = { value: { label: "Saldo", color: "#2563eb" } };
  const chartConfigPurchasesTrend: ChartConfig = { value: { label: "Compras", color: "#2563eb" } };
  const chartConfigPurchasesCategory: ChartConfig = { total: { label: "Total", color: "#16a34a" } };
  const chartConfigDiagnosticsByType: ChartConfig = { count: { label: "Cantidad", color: "#2563eb" } };
  const chartConfigConvertedVsNot: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigDiscountTrend: ChartConfig = { value: { label: "Total", color: "#2563eb" } };
  const chartConfigDiscountShare: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigFunnel: ChartConfig = { count: { label: "Cantidad", color: "#2563eb" } };
  const chartConfigLeadsSeries: ChartConfig = { value: { label: "Leads", color: "#2563eb" } };
  const chartConfigCoupons: ChartConfig = {
    generados: { label: "Generados", color: "#2563eb" },
    canjeados: { label: "Canjeados", color: "#16a34a" }
  };
  const chartConfigLeadsByTipo: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigLoyaltyLevels: ChartConfig = { value: { label: "Valor", color: "#2563eb" } };
  const chartConfigNewVsRecurrentes: ChartConfig = {
    nuevos: { label: "Nuevos", color: "#2563eb" },
    recurrentes: { label: "Recurrentes", color: "#16a34a" }
  };
  const chartConfigTopSpend: ChartConfig = { total: { label: "Total", color: "#0ea5e9" } };
  const chartConfigCouponsSeries: ChartConfig = { value: { label: "Cupones", color: "#a855f7" } };

  const resolveRange = useCallback((type: typeof rangeType) => {
    const now = new Date();
    let start: Date;
    let end: Date = new Date(now);

    if (type === 'week') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(now.getFullYear(), now.getMonth(), diff);
    } else if (type === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (type === 'lastmonth') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (type === 'quarter') {
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      start = new Date(now.getFullYear(), quarterStart, 1);
    } else if (type === 'year') {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (type === 'lastyear') {
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31);
    } else {
      start = new Date(now);
    }

    return { start: isoLocal(start), end: isoLocal(end) };
  }, []);

  useEffect(() => {
    if (rangeType === 'custom' && startDate && endDate) {
      return;
    }
    const nextRange = resolveRange(rangeType);
    setStartDate(nextRange.start);
    setEndDate(nextRange.end);
  }, [rangeType, resolveRange]);

  // silent = refresco de fondo (socket): no mostrar el estado de carga, si no
  // la pantalla entera parpadea y se ve como si se recargara la pagina.
  const fetchOverview = useCallback(async (opts?: { silent?: boolean }) => {
    if (!startDate || !endDate) return;
    const silent = opts?.silent === true;
    try {
      if (!silent) setLoading(true);
      const response = await axios.get('/api/reports/overview', {
        params: { startDate, endDate }
      });
      setReportData(response.data);
    } catch (error: any) {
      console.error('Error fetching reports overview:', error);
      if (!silent) toast.error('No se pudieron cargar los reportes');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [startDate, endDate]);

  const fetchEfficiency = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const { data } = await axios.get('/api/supply-efficiency/report', { params: { startDate, endDate } });
      setEfficiencyData(data);
    } catch {
      setEfficiencyData(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchOverview();
    fetchEfficiency();
  }, [fetchOverview, fetchEfficiency]);

  useEffect(() => {
    const socket = getAppSocket();
    let refreshTimer: number | null = null;

    const scheduleSilentRefresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchOverview({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchOverview]);

  const generatePDF = async () => {
    try {
      setGeneratingPDF(true);
      // Informe con identidad ROCAM: respeta el periodo seleccionado
      const response = await axios.get('/api/reports/informe-mensual', {
        params: { startDate, endDate },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `ROCAM-Informe-${startDate}_a_${endDate}.pdf`;
      const result = openPdf({ blob, filename });
      toast.success(result === 'downloaded' ? 'Informe descargado' : 'Informe generado');
    } catch (error: any) {
      console.error('Error generando informe:', error);
      toast.error(error.response?.data?.error || 'Error al generar el informe');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const periodLabel = useMemo(() => {
    if (!reportData?.period?.current?.start || !reportData?.period?.current?.end) return '';
    return `${reportData.period.current.start} a ${reportData.period.current.end}`;
  }, [reportData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-lg text-foreground">No se pudieron cargar los datos del reporte</p>
          <Button onClick={() => fetchOverview()}>Reintentar</Button>
        </div>
      </div>
    );
  }

  const heatmapData = reportData.appointments?.charts?.heatmap || [];
  const heatmapDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  const heatmapHours = Array.from({ length: 10 }, (_, i) => String(8 + i).padStart(2, '0'));
  const heatmapMap = new Map(heatmapData.map((item: any) => [`${item.day}-${item.hour}`, item.count]));
  const heatmapMax = heatmapData.reduce((max: number, item: any) => Math.max(max, item.count || 0), 0) || 1;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Reportes</h1>
            <p className="text-sm text-muted-foreground">Historico del periodo seleccionado {periodLabel && `(${periodLabel})`}</p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <PeriodPicker
              rangeType={rangeType}
              startDate={startDate}
              endDate={endDate}
              onPreset={(type) => setRangeType(type)}
              onRange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
                setRangeType('custom');
              }}
            />
            <Button variant="outline" onClick={generatePDF} disabled={generatingPDF}>
              {generatingPDF ? 'Generando...' : 'Informe del mes (PDF)'}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="inline-flex w-fit flex-wrap justify-start gap-1 h-auto">
            <TabsTrigger value="resumen">Resumen</TabsTrigger>
            <TabsTrigger value="ventas">Ventas</TabsTrigger>
            <TabsTrigger value="caja">Caja</TabsTrigger>
            <TabsTrigger value="clientes">Clientes</TabsTrigger>
            <TabsTrigger value="vehiculos">Vehículos</TabsTrigger>
            <TabsTrigger value="turnos">Turnos</TabsTrigger>
            <TabsTrigger value="diagnosticos">Diagnósticos</TabsTrigger>
            <TabsTrigger value="servicios">Servicios</TabsTrigger>
            <TabsTrigger value="productos">Productos</TabsTrigger>
            <TabsTrigger value="margen">Margen</TabsTrigger>
            <TabsTrigger value="compras">Compras</TabsTrigger>
            <TabsTrigger value="gastos">Gastos</TabsTrigger>
            <TabsTrigger value="insumos">Insumos</TabsTrigger>
            <TabsTrigger value="personal">Personal</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="space-y-6">
            <TableroGeneral onNavigate={setActiveTab} />
          </TabsContent>

          <TabsContent value="ventas" className="space-y-6">
            <VentasSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="caja" className="space-y-6">
            <CajaSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="personal" className="space-y-6">
            <PersonalSection />
          </TabsContent>

          <TabsContent value="clientes" className="space-y-6">
            <ClientesSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="vehiculos" className="space-y-6">
            <VehiculosSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="turnos" className="space-y-6">
            <TurnosSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="diagnosticos" className="space-y-6">
            <DiagnosticosSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="servicios" className="space-y-6">
            <ItemsSection tipo="servicios" startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="productos" className="space-y-6">
            <ItemsSection tipo="productos" startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="margen" className="space-y-6">
            <MargenSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="compras" className="space-y-6">
            <ComprasSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="gastos" className="space-y-6">
            <GastosSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          <TabsContent value="insumos" className="space-y-6">
            <InsumosSection startDate={startDate} endDate={endDate} />
          </TabsContent>

          </Tabs>
      </div>
    </div>
  );
};

export default Reports;
